import React, { useCallback, useRef, useState } from "react";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

/**
 * Confirmação com API imperativa, no lugar de `window.confirm`.
 *
 * O diálogo nativo do navegador é o pior padrão que restava na interface:
 * mostra a URL do servidor, ignora o tema, não distingue ação destrutiva de
 * ação comum e bloqueia a thread principal enquanto está aberto. Trocar
 * exigia, em cada página, dois estados e um componente — atrito suficiente
 * para ninguém trocar.
 *
 * O diálogo é próprio, e não o `ConfirmationModal` compartilhado, por um
 * motivo concreto: aquele chama `onClose(false)` ANTES de `onConfirm()`, o
 * que fazia a promise resolver `false` e a ação ser descartada em silêncio —
 * o pior tipo de falha, porque o usuário confirma e nada acontece.
 *
 * Uso:
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   ...
 *   if (!(await confirm({ title: "Excluir?", message: "Não dá pra desfazer." }))) return;
 *   ...
 *   return (<Container>{confirmDialog}...</Container>);
 */
const useConfirm = () => {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback(
    ({
      title = "Confirmar ação",
      message = "",
      confirmLabel = "Confirmar",
      cancelLabel = "Cancelar",
      destructive = true
    } = {}) =>
      new Promise(resolve => {
        resolverRef.current = resolve;
        setState({ title, message, confirmLabel, cancelLabel, destructive });
      }),
    []
  );

  const settle = useCallback(value => {
    setState(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(value);
  }, []);

  const dialog = (
    <Dialog
      open={!!state}
      onClose={() => settle(false)}
      maxWidth="xs"
      fullWidth
      aria-labelledby="use-confirm-title"
    >
      <DialogTitle id="use-confirm-title">{state?.title}</DialogTitle>
      {state?.message && (
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => settle(false)}>{state?.cancelLabel}</Button>
        <Button
          variant="contained"
          // Ação destrutiva em vermelho: quem está prestes a apagar algo
          // precisa ver isso no botão, não só ler no título.
          color={state?.destructive ? "secondary" : "primary"}
          onClick={() => settle(true)}
          autoFocus
        >
          {state?.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return [confirm, dialog];
};

export default useConfirm;
